import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  const handleNav = (href: string) => {
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/aa761934033d5a6075060e847e1fafc8.jpg"
          alt="新卒採用スカウト代行サービスのヒーロー背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1e33]/80 via-[#1a2e4a]/70 to-[#0f1e33]/75" />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 text-center transition-all duration-1000 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        {/* Eyebrow */}
        <div
          className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6 sm:mb-8 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: mounted ? '100ms' : '0ms' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#E8601C] inline-block" />
          <span className="text-white/90 text-xs tracking-widest uppercase font-medium">
            OfferBox / dodaキャンパス 対応
          </span>
        </div>

        {/* Main Heading */}
        <h1
          className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 sm:mb-6 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: "'Noto Serif JP', serif", transitionDelay: mounted ? '250ms' : '0ms' }}
        >
          新卒スカウトを、<br />
          <span className="text-white">確実な母集団形成へ。</span>
        </h1>

        {/* Sub Heading */}
        <p
          className={`text-white/80 text-sm sm:text-base md:text-lg leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: mounted ? '400ms' : '0ms' }}
        >
          ターゲット選定からスカウト送信まで専門チームが代行。<br className="hidden sm:block" />
          返信率にコミットした、新卒採用のための仕組みづくりを支援します。
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: mounted ? '550ms' : '0ms' }}
        >
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className="bg-[#E8601C] text-white text-sm font-medium px-8 py-3.5 rounded-full hover:bg-[#d55519] transition-colors duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            お問い合わせ
          </a>
          <a
            href="#service"
            onClick={(e) => { e.preventDefault(); handleNav('#service'); }}
            className="bg-transparent text-white text-sm font-medium px-8 py-3.5 rounded-full border border-white/50 hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            サービス詳細を見る
          </a>
        </div>

        {/* Trust Indicators */}
        <div
          className={`mt-10 sm:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/60 text-xs transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: mounted ? '700ms' : '0ms' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-building-2-line text-white/60" />
            </div>
            <span>2026年10社以上に新規導入</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/20" />
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-check-line text-white/60" />
            </div>
            <span>平均返信率 5%以上</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/20" />
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-white/60" />
            </div>
            <span>週15時間の工数削減</span>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/40 text-xs tracking-widest">SCROLL</span>
        <i className="ri-arrow-down-line text-white/40 text-lg" />
      </div>
    </section>
  );
}
